import { Dialog, DialogActions, DialogContent, FormHelperText, Stack, TextField, Typography } from "@mui/material";
import { MprcContainer, MprcDialogTitleClose, MprcFileSelection, MprcFormSubHeader, MprcRequestIndicator, MprcStringToHtml } from "components";
import { ButtonCancel, ButtonSave } from "components/buttons";
import { MprcDatePicker } from "components/MprcDatePicker";
import { MprcRequestErrorReport, MprcRequestSnackbarErrorReport, MprcResolveRequestErrors } from "components/MprcRequestErrorReport";
import { DevPlanDto } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import { enqueueSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

////////////////////////////////////////////////////////////////////////////////

interface IValidationFields {
  name?: string[] | null,
  description?: string[] | null,
  attach?: string[] | null,
  availableStarting?: string[] | null,
  completionDeadline?: string[] | null,
  archivingDeadline?: string[] | null,
}

interface IPros {
  devPlanId: string | null;
  userId?: string;
  name?: string;
  onClose: (devPlanId?: string | null) => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const EditDevPlanDialog: React.FC<IPros> = (props) => {

  const api = useCoreApi();
  const session = useSession();
  const { t } = useTranslation("lms");

  const { devPlanId, userId, name, onClose } = props;

  const [viewData, setViewData] = useState<DevPlanDto>({} as DevPlanDto);
  const [loadDto, setLoadDto] = useState<DevPlanDto>();
  const [error, setError] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [recordChanged, setRecordChanged] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [formValidation, setFormValidation] = useState<IValidationFields>({
    name: undefined
  });

  const canSave = recordChanged && Object.values(formValidation).filter(p => p !== null).length === 0;

  ///
  const handleSave = () => {
    setSaving(true);
    api.v1.devPlan.createOrUpdateDevPlan({
      devPlanId: devPlanId ?? null,
      name: viewData.name,
      description: viewData.description,
      availableStarting: viewData.availableStarting,
      completionDeadline: viewData.completionDeadline,
      archivingDeadline: viewData.archivingDeadline,
      userId: userId ?? session.user.userId,
      attachId: viewData.attach?.fileId ?? null
    })
      .then(response => {
        if (response.data != null) {
          enqueueSnackbar(t("Record.SaveSuccessMessage", { ns: "shared" }), { variant: "success" });
          onClose(response.data);
        }
        else {
          enqueueSnackbar(t("Record.FailSuccessMessage", { ns: "shared" }), { variant: "error" });
        }
      })
      .catch(e => {
        const errors = MprcResolveRequestErrors(e);
        const fieldErrors = errors.fieldsWithError;
        if (fieldErrors) {

          enqueueSnackbar(t("Record.SaveFailedWithFieldErrorsMessage", { ns: "shared" }), { variant: "error" });

          setFormValidation({
            ...formValidation,
            name: fieldErrors["name"] ?? formValidation.name ?? null,
            description: fieldErrors["description"] ?? formValidation.description ?? null,
            archivingDeadline: fieldErrors["archivingDeadline"] ?? formValidation.archivingDeadline ?? null,
            availableStarting: fieldErrors["availableStarting"] ?? formValidation.availableStarting ?? null,
            completionDeadline: fieldErrors["completionDeadline"] ?? formValidation.completionDeadline ?? null,
            attach: fieldErrors["attachId"] ?? formValidation.attach ?? null,
          });
        }
        else {
          MprcRequestSnackbarErrorReport(e);
        }
      })
      .finally(() => setSaving(false));
  };

  ///
  const validateForm = (data: DevPlanDto) => {
    let newFormValidation = { ...formValidation };
    newFormValidation.name = data.name != undefined && data.name.length == 0 ? [t("Messages.RequiredFieldText", { ns: "shared" })] : null;

    setFormValidation(newFormValidation);
  };

  ///
  const handleUpdateRecord = (name: keyof DevPlanDto, v: any) => {
    const newViewData = { ...viewData, [name]: v };

    validateForm(newViewData);
    setViewData(newViewData);
  };

  useEffect(() => {
    if (devPlanId) {
      setLoading(true);
      api.v1.devPlan.getDevPlan({ devPlanId })
        .then(response => {
          setViewData(response.data);
          setLoadDto(response.data);
          validateForm(response.data);
        })
        .catch(e => setError(e))
        .finally(() => setLoading(false));
    }
  }, [devPlanId]);

  useEffect(() => {
    setRecordChanged(!LibUtils.deepCompare(viewData, loadDto));
  }, [viewData]);

  return (
    <>
      <MprcRequestIndicator visible={loading || saving} type={saving ? "post" : "request"} />
      <Dialog open fullWidth maxWidth="sm" onClose={() => onClose()}>
        <MprcDialogTitleClose
          title={devPlanId == null ? t("DevPlan.Edit.TitleNew") : t("DevPlan.Edit.TitleEdit")}
          subtitle={name}
          onClose={() => onClose()}
          shrinkPadding
        />
        <DialogContent>
          <MprcRequestErrorReport error={error} />
          <TextField
            fullWidth
            required
            name="name"
            autoFocus
            label={t("DevPlan.Edit.NameLabel")}
            margin="dense"
            slotProps={{
              input: {
                inputProps: { maxLength: 255 }
              }
            }}
            onChange={(e) => handleUpdateRecord("name", e.target.value)}
            value={viewData.name || ""}
            error={!!formValidation.name}
            helperText={!!formValidation.name && <MprcStringToHtml text={formValidation.name} />}
          />
          <MprcContainer>
            <TextField
              fullWidth
              name="description"
              label={t("DevPlan.Edit.DescriptionLabel")}
              margin="dense"
              multiline
              slotProps={{
                input: {
                  inputProps: { maxLength: 512 }
                }
              }}
              onChange={(e) => handleUpdateRecord("description", e.target.value)}
              value={viewData.description || ""}
              error={!!formValidation.description}
              helperText={!!formValidation.description && <MprcStringToHtml text={formValidation.description} />}
            />
          </MprcContainer>
          <MprcContainer>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Stack alignItems="start">
                <Typography variant="overline">{t("DevPlan.Edit.AvailableStartingLabel")}</Typography>
                <MprcDatePicker
                  disablePast
                  value={viewData.availableStarting || null}
                  placeholder={t("DevPlan.Edit.ImmediateText")}
                  timePartHour="initialDate"
                  onChange={(date, validate) => {
                    handleUpdateRecord("availableStarting", date);
                    setFormValidation(prevState => ({ ...prevState, availableStarting: validate ? null : [""] }));
                  }}
                  error={formValidation.availableStarting != null}
                  helperText={!!formValidation.availableStarting && <MprcStringToHtml text={formValidation.availableStarting} />}
                />
              </Stack>
              <Stack alignItems="start">
                <Typography variant="overline">{t("DevPlan.Edit.CompletionDeadlineLabel")}</Typography>
                <MprcDatePicker
                  disablePast
                  minDate={viewData.availableStarting ?? undefined}
                  value={viewData.completionDeadline || null}
                  placeholder={t("DevPlan.Edit.WithoutDeadlineText")}
                  timePartHour="endDate"
                  onChange={(date, validate) => {
                    handleUpdateRecord("completionDeadline", date);
                    setFormValidation(prevState => ({ ...prevState, completionDeadline: validate ? null : [""] }));
                  }}
                  error={formValidation.completionDeadline != null}
                  helperText={!!formValidation.completionDeadline && <MprcStringToHtml text={formValidation.completionDeadline} />}
                />
              </Stack>
              <Stack alignItems="start">
                <Typography variant="overline">{t("DevPlan.Edit.ArchivingDeadlineLabel")}</Typography>
                <MprcDatePicker
                  disablePast
                  minDate={viewData.completionDeadline ?? viewData.availableStarting ?? undefined}
                  value={viewData.archivingDeadline || null}
                  placeholder={t("DevPlan.Edit.WithoutDeadlineText")}
                  timePartHour="endDate"
                  onChange={(date, validate) => {
                    handleUpdateRecord("archivingDeadline", date);
                    setFormValidation(prevState => ({ ...prevState, archivingDeadline: validate ? null : [""] }));
                  }}
                  error={formValidation.archivingDeadline != null}
                  helperText={!!formValidation.archivingDeadline && <MprcStringToHtml text={formValidation.archivingDeadline} />}
                />
              </Stack>
            </Stack>
          </MprcContainer>
          <MprcContainer>
            <MprcFormSubHeader
              title={t("DevPlan.Edit.AttachSubTitle")}
              subtitle={t("DevPlan.Edit.AttachHint")}
              titleVariant="overline"
            />
          </MprcContainer>
          <MprcFileSelection
            selectedFile={viewData.attach}

            onFileUploadStarted={
              () => {
                setFormValidation(prevState => ({ ...prevState, attach: [""] }));
                handleUpdateRecord("attach", undefined);
              }
            }
            onFileUploadError={() => {
              handleUpdateRecord("attach", null);
            }}
            onFileUploadFinished={() => setFormValidation(prevState => ({ ...prevState, attach: null }))}
            onFileUploadSuccess={(file) => handleUpdateRecord("attach", file.mainFile)}
            onClear={() => handleUpdateRecord("attach", null)}
          />
          <FormHelperText error={viewData.attach != undefined && !!formValidation.attach}>
            <MprcStringToHtml text={formValidation?.attach} color="error" />
          </FormHelperText>
        </DialogContent>
        <DialogActions>
          <ButtonCancel variant="contained" onClick={() => onClose()} />
          <ButtonSave
            disabled={!canSave}
            variant="contained"
            onClick={handleSave}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
